import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { quickSearchEventV2, searchFileContentEvent } from 'services/generalServices';
import { openLinkInBlank, removeHtmlTags, removePhoneNumberFormatting } from 'utils/utils';
import { useHistory } from 'react-router-dom';
import { contactType } from 'common/dropdownOptions';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useToggle } from 'hooks/useToggle';
import { getFileExtension, getFileType } from 'utils/filesUtils';
import axios from 'axios';
import GlobalTooltip from 'components/GlobalTooltip';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getTimezoneOffset } from 'utils/utility_functions/timezone';
import { MultiSelect } from 'primereact/multiselect';
import {
  GLOBAL_SEARCH_FILTER_OPTIONS,
  GLOBAL_SEARCH_FILTER_TO_MODULES_MAP,
  GLOBAL_SEARCH_FILTER_TO_RES_TYPE_MAP,
} from 'utils/searchConstants';
import { mapQuickSearchResults } from './utils/mapQuickSearchResults';
import constants from 'constants/index';

export default function GlobalSearch() {
  const { checkSubPermission, userDetails, firmPhoneNumber, getPermission } = useUserDetailsContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const wrapperRef = useRef(null);
  const showContentRef = useRef(null);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showContent, toggleContent] = useToggle(false);
  const cancelTokenSource = useRef(null);
  const delayedSearchRef = useRef(null);
  const skipNextEffectRef = useRef(false);
  const requestIdRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);

  // Filter options based on permissions + admin role
  const filterOptions = useMemo(() => {
    return GLOBAL_SEARCH_FILTER_OPTIONS.filter((option) => {
      let hasSubscriptionAccess = true;
      let hasUserAccess = true;
      switch (option.value) {
        case 'task':
          hasSubscriptionAccess = checkSubPermission('task_management');
          break;
        case 'events':
          hasSubscriptionAccess = checkSubPermission('calendaring');
          break;
        case 'files':
          break;
        case 'cases':
          hasSubscriptionAccess = checkSubPermission('case_management');
          hasUserAccess = getPermission('cases') !== constants.permissions.HIDDEN;
          break;
        case 'leads':
          hasSubscriptionAccess = checkSubPermission('lead_management');
          hasUserAccess = getPermission('leads') !== constants.permissions.HIDDEN;
          break;
        case 'contacts':
          hasSubscriptionAccess = checkSubPermission('contact_management');
          break;
        case 'case_note':
          hasSubscriptionAccess = checkSubPermission('case_management');
          hasUserAccess = getPermission('cases') !== constants.permissions.HIDDEN;
          break;
        case 'email_threads':
          hasSubscriptionAccess = checkSubPermission('email_service') && !!userDetails?.business_email;
          break;
        case 'messages':
          hasSubscriptionAccess = checkSubPermission('secure_messages');
          break;
        case 'text_messages':
          hasSubscriptionAccess = checkSubPermission('texting');
          break;
        default:
          break;
      }
      return hasSubscriptionAccess && hasUserAccess;
    });
  }, [checkSubPermission, getPermission, userDetails?.business_email, userDetails?.access_level]); // eslint-disable-line

  // Map UI filter to API modules string
  const filterToModulesMap = GLOBAL_SEARCH_FILTER_TO_MODULES_MAP;
  // Map UI filter to quick-search res_type
  const filterToResTypeMap = GLOBAL_SEARCH_FILTER_TO_RES_TYPE_MAP;

  function getModulesParam(filtersArg = selectedFilters) {
    if (!filtersArg || filtersArg.length === 0) return '';
    return (filtersArg || [])
      .map((f) => filterToModulesMap[f])
      .filter(Boolean)
      .join(',');
  }

  function getResTypeParam(filtersArg = selectedFilters) {
    // quick-search-v2 res_type is an array
    if (!filtersArg || filtersArg.length === 0) return [];
    const resTypes = filtersArg
      .map((f) => {
        const resType = filterToResTypeMap[f];
        // When case_note is selected, return both variants
        if (f === 'case_note' && resType === 'case_note') {
          return ['case_note', 'case-note'];
        }
        return resType;
      })
      .filter(Boolean);
    // Flatten the array in case we have nested arrays (from case_note)
    return resTypes.flat();
  }

  const shouldShow = (key) => !selectedFilters?.length || selectedFilters?.includes(key);

  function findType(item) {
    return getFileType(getFileExtension(item?.file_name));
  }

  const fetchData = useCallback(
    async (query, filtersArg) => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel();
      }
      cancelTokenSource.current = axios.CancelToken.source();

      const requestId = ++requestIdRef.current;

      setLoading(true);
      try {
        // Use quick-search when showContent is false, otherwise use global-search
        if (!showContentRef.current) {
          const res_type = getResTypeParam(filtersArg);
          const timezoneOffset = userDetails?.timezone ? getTimezoneOffset(userDetails.timezone) : null;
          const res = await quickSearchEventV2(
            query,
            true,
            { cancelToken: cancelTokenSource.current.token },
            res_type,
            '',
            timezoneOffset,
            firmPhoneNumber || null
          );

          if (res.data && requestId === requestIdRef.current) {
            const quickSearchResults = mapQuickSearchResults(res.data);
            setResults(quickSearchResults);
          }
        } else {
          // Use new search-file-content API for content search
          const res = await searchFileContentEvent(query, [], 20, 0, '', true, cancelTokenSource.current.token);
          if (res.data && showContentRef.current && requestId === requestIdRef.current) {
            const data = (res.data?.results || []).map((v) => ({
              ...v,
              object_id: Array.isArray(v.file_id) ? v.file_id[0] : v.file_id,
              file_path: null,
              type: findType(v),
            }));
            setResults(data);
          }
        }
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Previous request canceled:', error.message);
        } else {
          console.error(error);
          if (requestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      }
    },
    [userDetails, firmPhoneNumber]
  ); // eslint-disable-line

  const delayedSearch = useCallback(
    debounce((query, filtersArg) => {
      if (query !== '') {
        fetchData(removePhoneNumberFormatting(query), filtersArg);
      } else {
        if (cancelTokenSource.current) {
          cancelTokenSource.current.cancel();
          cancelTokenSource.current = null;
        }
        setResults({});
        setLoading(false);
      }
    }, 500),
    [selectedFilters, fetchData] // eslint-disable-line
  );

  useEffect(() => {
    delayedSearchRef.current = delayedSearch;
  }, [delayedSearch]);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    if (delayedSearchRef.current) {
      delayedSearchRef.current.cancel();
    }
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
      cancelTokenSource.current = null;
    }

    setLoading(true);
    setResults({});
    delayedSearch(query, selectedFilters);
  }, [query, selectedFilters]); //eslint-disable-line

  useEffect(() => {
    showContentRef.current = showContent;
  }, [showContent]); //eslint-disable-line

  // Detect mobile viewport to ensure module dropdown shows with search bar
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 991.98px)');
    const update = () => setIsMobile(Boolean(mq.matches));
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
    } else if (mq.addListener) {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', update);
      } else if (mq.removeListener) {
        mq.removeListener(update);
      }
    };
  }, []);

  const redirectToPage = (pathname, state, search) => {
    if (!openLinkInBlank(pathname, state)) {
      history?.replace({
        pathname,
        state,
        search,
      });
    }
  };

  const cleanSk = (sk) => {
    if (!sk) return sk;
    const parts = sk.split('#');
    if (parts.length >= 3 && parts[1] === 'firm_user') {
      return parts.slice(1).join('#');
    }
    return sk;
  };

  const goToDetails = (data) => {
    if (data?.res_type === 'case' || data?.res_type === 'lead') {
      if (data?.is_lead || data?.res_type === 'lead') {
        return `/leads/${data?.case_id}`;
      } else {
        return `/cases/${data?.case_id}`;
      }
    } else if (data?.res_type === 'message') {
      return `/messages/${data?.message_thread_id}`;
    } else if (data?.res_type === 'message-reply') {
      return `/messages/${data?.pk?.split('#')?.[1]}`;
    } else if (data?.res_type === 'email_thread') {
      // Redirect to mail-logs and open the thread preview
      return {
        pathname: '/mail-logs',
        state: {
          emailThread: {
            pk: data?.pk,
            sk: data?.sk,
          },
        },
      };
    } else if (data?.res_type === 'text_message_thread' || data?.res_type === 'text_message_reply') {
      // Get contact_id only from contact_id field
      const contactId = data?.contact_id;

      // Try to get phone number from multiple sources
      let phoneNumber = data?.to || data?.To || data?.subtitle;

      // If phone number is a number, convert to string
      if (typeof phoneNumber === 'number') {
        phoneNumber = String(phoneNumber);
      }

      // If still no phone number, try to extract from pk (format: tenant_id#phone_number)
      if (!phoneNumber && data?.pk) {
        const pkParts = data.pk.split('#');
        if (pkParts.length > 1) {
          phoneNumber = pkParts[pkParts.length - 1];
        }
      }

      // Ensure phone number has + prefix if it's a valid number
      if (phoneNumber && !phoneNumber.startsWith('+')) {
        // Remove all non-digits, then add + prefix
        const digitsOnly = phoneNumber.replace(/\D/g, '');
        if (digitsOnly) {
          phoneNumber = '+' + digitsOnly;
        }
      }

      // If we have both contact_id and phone number, navigate to the specific thread
      if (contactId && phoneNumber) {
        return `/text-message/${contactId}/${phoneNumber}?tab=0`;
      } else if (contactId && !phoneNumber) {
        // If we have contact_id but no phone number, navigate to text messages list
        // The contact_id might help filter or the page can handle it
        return '/text-messages';
      } else if (phoneNumber && !contactId) {
        // If we only have phone number but no contact_id, navigate to text messages list
        // Note: pk format is tenant_id#phone_number, so first part is tenant_id, not contact_id
        // contact_id should be provided by API for proper navigation
        return '/text-messages';
      } else {
        // If neither contact_id nor phone number is available, navigate to text messages list
        return '/text-messages';
      }
    } else if (data?.res_type === 'file_system_entry' || data?.res_type?.includes('file_system_entry') || showContentRef.current) {
      localStorage.setItem('viewFile', JSON.stringify(data));

      let fileId = data?.object_id || data?.file_id || data?.document_id;
      if (!fileId && data?.sk) {
        const skParts = data.sk.split('#');
        fileId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
      }

      // Ensure file has object_id set for FileManagerView to find it
      const fileData = {
        ...data,
        object_id: fileId,
      };

      // Add file_id query parameter for FileManagerView
      const searchParam = fileId ? `?file_id=${fileId}` : '';

      // Pass res_type_id in state to detect folder changes
      const state = {
        file: fileData,
        res_type_id: data?.res_type_id || fileId,
      };

      // If file is inside a folder, navigate to that folder so the file can be found.
      const folderId = data?.folder_id || data?.folderId;
      const folderName = data?.folder_name || data?.folderName;
      if (folderId) {
        if (folderName) {
          state.breadCrumb = { label: folderName, value: folderId };
        } else {
          state.folderId = folderId;
        }
      }

      if (data?.case_id && data?.case_id !== 'undefined') {
        return {
          pathname: `/${data?.is_lead ? 'leads' : 'cases'}/${data?.case_id}/files`,
          search: searchParam,
          state: state,
        };
      } else {
        // For quick-search-v2 and global-search-v2, redirect to firm-documents when case_id is not present
        return {
          pathname: `/file-manager/firm-documents`,
          search: searchParam,
          state: state,
        };
      }
    } else if (data?.res_type === 'event') {
      return {
        pathname: '/calendar',
        search: `?id=${encodeURIComponent(data?.sk)}`,
      };
    } else if (data?.res_type === 'user') {
      if (data?.access_level && data?.access_level !== 'client') {
        return `/settings/team-management/${data?.user_id}`;
      } else if (contactType.includes(data?.contact_type || !data?.access_level || data?.access_level === 'client')) {
        return `/contacts/${data?.contact_id}`;
      }
    } else if (data?.res_type === 'task') {
      let taskId = data?.task_id;
      if (!taskId && data?.sk) {
        const skParts = data.sk.split('#');
        taskId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
      }

      const searchParam = taskId ? `?id=${taskId}` : '';
      return {
        pathname: '/tasks',
        search: searchParam,
        state: { viewTask: data },
      };
    } else if (data?.res_type === 'invoice') {
      let invoiceId = null;
      if (data?.sk && data.sk.includes('#')) {
        const skParts = data.sk.split('#');
        invoiceId = skParts[skParts.length - 1];
      }

      const invoiceSk = invoiceId ? `invoice#${invoiceId}` : null;
      const searchParam = invoiceSk ? `?invoice_sk=${encodeURIComponent(invoiceSk)}` : '';

      return {
        pathname: `/cases/${data?.case_id ? data?.case_id : data?.invoice?.case_id}/billing`,
        search: searchParam,
      };
    } else if (data?.res_type === 'payment_request') {
      let paymentRequestId = null;
      if (data?.sk && data.sk.includes('#')) {
        const skParts = data.sk.split('#');
        paymentRequestId = skParts[skParts.length - 1];
      }

      const paymentRequestSk = paymentRequestId ? `payment_request#${paymentRequestId}` : null;
      const searchParam = paymentRequestSk ? `?payment_request_sk=${encodeURIComponent(paymentRequestSk)}` : '';

      return {
        pathname: `/cases/${data?.case_id}/billing`,
        search: searchParam,
      };
    } else if (data?.res_type === 'case-note') {
      let caseNoteId = data?.case_note_id;
      if (!caseNoteId && data?.sk) {
        const skParts = data.sk.split('#');
        caseNoteId = skParts.length > 1 ? skParts[skParts.length - 1] : null;
      }

      const noteState = { case_note: data, pk: data?.pk, sk: data?.sk };
      const pathname = caseNoteId ? `/cases/${data?.case_id}/notes/${caseNoteId}` : `/cases/${data?.case_id}/notes`;
      return { pathname, state: noteState };
    }
    return '/';
  };

  //close search when clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      const target = event.target;
      // Ignore clicks inside PrimeReact overlay panels (e.g., Dropdown panel)
      if (
        target?.closest?.('.p-dropdown-panel') ||
        target?.closest?.('.p-dropdown') ||
        target?.closest?.('.p-multiselect-panel') ||
        target?.closest?.('.p-multiselect') ||
        target?.closest?.('.p-overlaypanel')
      ) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        close();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  const close = () => {
    setLoading(false);
    setResults({});
    setIsOpen(false);
    setQuery('');
  };
  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      delayedSearch(query, selectedFilters);
    }
  };

  const loader = () => {
    return (
      <div className="overlay searchbutton">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1em' }}></i>
      </div>
    );
  };

  return (
    <>
      <div className={`search-container ${isOpen && isMobile ? 'mobile-open' : ''}`} ref={wrapperRef}>
        {isOpen && (
          <span className="search-filter me-0 d-inline-flex align-items-center">
            <MultiSelect
              value={selectedFilters}
              options={filterOptions}
              onChange={(e) => {
                skipNextEffectRef.current = true;
                setSelectedFilters(e.value || []);
                if (query) {
                  if (delayedSearchRef.current) {
                    delayedSearchRef.current.cancel();
                  }
                  setLoading(true);
                  setResults({});
                  fetchData(removePhoneNumberFormatting(query), e.value || []);
                }
              }}
              optionLabel="label"
              optionValue="value"
              appendTo={typeof document !== 'undefined' ? document.body : null}
              placeholder="All"
              display="chip"
              showClear
              filter
              className="w-100"
              style={{ width: 250 }}
              disabled={showContent}
            />
          </span>
        )}
        <span className="p-input-icon-left autowidth d-inline-flex">
          <TextSnippetWrapper
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'flex',
              paddingRight: '60px',
            }}
            type="text"
            className={isOpen ? 'open search expandright input-style with-filter' : 'search expandright input-style'}
            id="searchright"
            autoComplete="off"
            placeholder="Search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value || '');
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={onSearchKeyDown}
          />
        </span>
        {checkSubPermission('full_text_search') && isOpen && (
          <span
            role="button"
            onClick={() => {
              if (isOpen && query) {
                setLoading(true);
              }
              setResults({});
              toggleContent();
              delayedSearch(query, selectedFilters);
            }}
          >
            <GlobalTooltip tooltip="Search Content Data" className="me-1 fa-lg">
              <i className={`icon-files fa-lg me-lg-0 me-md-4 me-2 ${showContent ? 'highlight' : ''}`} />
            </GlobalTooltip>
          </span>
        )}
        {loading ? (
          loader()
        ) : (
          <label
            className="button searchbutton pointer"
            htmlFor="searchright"
            onClick={() => {
              setIsOpen(true);
              if (isOpen && query) {
                delayedSearch(query, selectedFilters);
              }
            }}
          >
            <GlobalTooltip tooltip="Search">
              <i className="icon-search fa-lg" />
            </GlobalTooltip>
          </label>
        )}
        {Object.keys(results || {})?.length > 0 && (
          <div className={isOpen ? 'result open' : 'result'}>
            {!showContentRef.current ? (
              <div className="result-list">
                {shouldShow('contacts') &&
                  Boolean(
                    results?.user?.filter(
                      (v) => (v.access_level && v.access_level === 'client') || !v.access_level || contactType.includes(v?.contact_type)
                    ).length > 0
                  ) && (
                    <div className="list">
                      <h1 className="title">Contacts</h1>
                      {results?.user
                        ?.filter(
                          (v) => (v.access_level && v.access_level === 'client') || !v.access_level || contactType.includes(v?.contact_type)
                        )
                        .map((v, i) => (
                          <li key={i}>
                            <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                              <h1 className="sub-title text-primary-dark">
                                {v.contact_type === 'person' ? `${v?.first_name} ${v?.last_name}` : v?.title}
                              </h1>
                            </Link>
                          </li>
                        ))}
                    </div>
                  )}

                {shouldShow('cases') && Boolean(results?.case?.filter((v) => !Boolean(v?.is_lead)).length > 0) && (
                  <div className="list">
                    <h1 className="title">Cases</h1>
                    {results?.case
                      ?.filter((v) => !Boolean(v?.is_lead))
                      .map((v, i) => (
                        <li key={i}>
                          <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                            <h1 className="sub-title text-primary-dark">{v?.case_description?.case_name}</h1>
                          </Link>
                        </li>
                      ))}
                  </div>
                )}

                {(() => {
                  const shouldShowLead = shouldShow('leads'); // Use 'leads' (plural) to match filter key
                  const hasLeads = Boolean(results?.lead?.length > 0);
                  return (
                    shouldShowLead &&
                    hasLeads && (
                      <div className="list">
                        <h1 className="title">Leads</h1>
                        {results?.lead?.map((v, i) => (
                          <li key={i}>
                            <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                              <h1 className="sub-title text-primary-dark">
                                {v?.case_description?.case_name || v?.title || v?.display_title || '-'}
                              </h1>
                            </Link>
                          </li>
                        ))}
                      </div>
                    )
                  );
                })()}

                {shouldShow('files') && Boolean(results?.file_system_entry?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Files</h1>
                    {results?.file_system_entry?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.display_name}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}

                {shouldShow('firm_users') &&
                  Boolean(results?.user?.filter((v) => v.access_level && v.access_level !== 'client').length > 0) && (
                    <div className="list">
                      <h1 className="title">Firm Users</h1>
                      {results?.user
                        ?.filter((v) => v.access_level && v.access_level !== 'client')
                        .map((v, i) => (
                          <li key={i}>
                            <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                              <h1 className="sub-title text-primary-dark">
                                {v?.first_name} {v?.last_name}
                              </h1>
                            </Link>
                          </li>
                        ))}
                    </div>
                  )}

                {shouldShow('events') && Boolean(results?.event?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Events</h1>
                    {results?.event?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.title}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}

                {shouldShow('messages') && (Boolean(results?.message?.length > 0) || Boolean(results?.['message-reply']?.length > 0)) && (
                  <div className="list">
                    <h1 className="title">Messages</h1>
                    {results?.message?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.subject}</h1>
                        </Link>
                      </li>
                    ))}
                    {results?.['message-reply']?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{removeHtmlTags(v?.message)}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}
                {shouldShow('email_threads') && Boolean(results?.email_thread?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Email Threads</h1>
                    {results?.email_thread?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{v?.body ? v?.body : v?.title}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}

                {shouldShow('text_messages') &&
                  (Boolean(results?.text_message_thread?.length > 0) || Boolean(results?.['text_message_reply']?.length > 0)) && (
                    <div className="list">
                      <h1 className="title">Text Messages</h1>
                      {results?.text_message_thread?.map((v, i) => (
                        <li key={i}>
                          <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                            <h1 className="sub-title text-primary-dark">{v?.body ? v?.body : v?.title}</h1>
                          </Link>
                        </li>
                      ))}
                      {results?.['text_message_reply']?.map((v, i) => (
                        <li key={i}>
                          <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                            <h1 className="sub-title text-primary-dark">{v?.body ? v?.body : v?.title}</h1>
                          </Link>
                        </li>
                      ))}
                    </div>
                  )}

                {shouldShow('task') && Boolean(results?.task?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Task</h1>
                    {results?.task?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark ">
                            {v?.case_name ? v?.case_name + ': ' : ''} {v?.task_name}
                          </h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}
                {shouldShow('payment_request') && Boolean(results?.payment_request?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Payment Request</h1>
                    {results?.payment_request?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">
                            {v?.case_name ? v?.case_name + ': ' : ''} {v?.payment_request_number}
                          </h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}
                {shouldShow('invoice') && Boolean(results?.invoice?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Invoice</h1>
                    {results?.invoice?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">
                            {v?.invoice?.case_name ? v?.invoice?.case_name + ': ' : ''} {v?.invoice_number}
                          </h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}
                {shouldShow('case_note') && Boolean(results?.['case-note']?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Case Notes</h1>
                    {results?.['case-note']?.map((v, i) => {
                      const linkPath = goToDetails(v);
                      return (
                        <li key={i}>
                          {linkPath ? (
                            <Link to={linkPath} onClick={() => close()} className="text-decoration-none">
                              <h1 className="sub-title text-primary-dark">{v?.title || '-'}</h1>
                            </Link>
                          ) : (
                            <h1 className="sub-title text-primary-dark">{v?.title || '-'}</h1>
                          )}
                        </li>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="result-list">
                {shouldShow('files') && Boolean(results?.length > 0) && (
                  <div className="list">
                    <h1 className="title">Files</h1>
                    {results?.map((v, i) => (
                      <li key={i}>
                        <Link to={() => goToDetails(v)} onClick={() => close()} className="text-decoration-none">
                          <h1 className="sub-title text-primary-dark">{decodeURIComponent(v?.file_name)}</h1>
                        </Link>
                      </li>
                    ))}
                  </div>
                )}
              </div>
            )}
            <hr className="my-1" />
            <Link
              to={`/search?search=${query}${showContentRef.current ? `&showContent=true` : ''}${
                selectedFilters?.length ? `&filter=${selectedFilters.join(',')}` : ''
              }`}
              className="redirection-link d-flex justify-content-center"
              onClick={() => setIsOpen(false)}
            >
              View All
            </Link>
          </div>
        )}
        {Object.keys(results || {})?.length === 0 && query?.length > 0 && loading === false && (
          <div className={isOpen ? 'result open' : 'result'}>
            <div className="list p-0 m-0">
              <h1 className="sub-title m-0">No Data Found</h1>
            </div>
          </div>
        )}
        {isOpen && isMobile && (
          <span className="search-close-btn d-flex justify-content-center align-items-center" onClick={() => setIsOpen(false)}>
            <i className="bi bi-x"></i>
          </span>
        )}
      </div>
    </>
  );
}
